import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react";

import { CalendarDateRange, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { RolesEnum, useGetProviderListByRoleQuery } from "@api/graphql/types-and-hooks";
import { useAllSessionClients } from "@hooks/useAllSessionClients";
import { useAdminData } from "@providers/AdminDataProvider";

export type VideoAnalyticsTabType = "Overview" | "My Metrics" | "BCBAs" | "RBTs";
export type DialogModalTabType = "Chart" | "Raw Data";

interface VideoAnalyticsContextI {
  selectedAnalyticTab: VideoAnalyticsTabType | null;
  setSelectedAnalyticTab: (val: VideoAnalyticsTabType) => void;
  isAllBCBAFetching: boolean;
  isAllRBTFetching: boolean;
  allBCBAList: SearchableDropdownItem[];
  allRBTList: SearchableDropdownItem[];
  selectedDialogTab: DialogModalTabType | null;
  setSelectedDialogTab: (val: DialogModalTabType) => void;
  sessionClients: SearchableDropdownItem[];
  isSessionClientsLoading: boolean;
  selectedDates: CalendarDateRange | undefined;
  setSelectedDates: React.Dispatch<React.SetStateAction<CalendarDateRange | undefined>>;
  selectedBCBA: SearchableDropdownItem[];
  setSelectedBCBA: React.Dispatch<React.SetStateAction<SearchableDropdownItem[]>>;
  selectedRBTs: SearchableDropdownItem[];
  setSelectedRBTs: React.Dispatch<React.SetStateAction<SearchableDropdownItem[]>>;
  selectedRBTDates: CalendarDateRange | undefined;
  setSelectedRBTDates: React.Dispatch<React.SetStateAction<CalendarDateRange | undefined>>;
}

const VideoAnalyticsContext = createContext<VideoAnalyticsContextI | undefined>(undefined);

export const useVideoAnalytics = () => {
  const context = useContext(VideoAnalyticsContext);
  if (context === undefined) {
    throw new Error("useVideoAnalytics must be used within a VideoAnalyticsProvider");
  }
  return context;
};

export const VideoAnalyticsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { isUserBCBA } = useAdminData();
  const { clients: sessionClients, isLoading: isSessionClientsLoading } = useAllSessionClients();

  const [selectedAnalyticTab, setSelectedAnalyticTab] = useState<VideoAnalyticsTabType | null>(null);
  const [selectedDialogTab, setSelectedDialogTab] = useState<DialogModalTabType>("Raw Data");

  const [allBCBAList, setAllBCBAList] = useState<SearchableDropdownItem[]>([]);
  const [allRBTList, setAllRBTList] = useState<SearchableDropdownItem[]>([]);

  const [selectedDates, setSelectedDates] = useState<CalendarDateRange | undefined>({});
  const [selectedBCBA, setSelectedBCBA] = useState<SearchableDropdownItem[]>([]);
  const [selectedRBTs, setSelectedRBTs] = useState<SearchableDropdownItem[]>([]);
  const [selectedRBTDates, setSelectedRBTDates] = useState<CalendarDateRange | undefined>({});

  const { data: allBCBAs, isFetching: isAllBCBAFetching } = useGetProviderListByRoleQuery(
    {
      roles: [RolesEnum.Bcba],
    },
    {
      queryKey: ["getProviderListByRole", RolesEnum.Bcba],
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !isUserBCBA,
    },
  );

  const { data: allRBTs, isFetching: isAllRBTFetching } = useGetProviderListByRoleQuery(
    {
      roles: [RolesEnum.Rbt],
    },
    {
      queryKey: ["getProviderListByRole", RolesEnum.Rbt],
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  useEffect(() => {
    if (allBCBAs) {
      setAllBCBAList(
        (allBCBAs.getProviderListByRole?.edges.map((item) => ({
          id: item?.node?.id,
          label: `${item?.node?.providermetadata?.firstName || "N/A"} ${item?.node?.providermetadata?.lastName || ""}`,
        })) as SearchableDropdownItem[]) || [],
      );
    }
  }, [allBCBAs]);
  useEffect(() => {
    if (allRBTs) {
      setAllRBTList(
        (allRBTs?.getProviderListByRole?.edges?.map((item) => ({
          id: item?.node?.id,
          label: `${item?.node?.providermetadata?.firstName || "N/A"} ${item?.node?.providermetadata?.lastName || ""}`,
        })) as SearchableDropdownItem[]) || [],
      );
    }
  }, [allRBTs]);

  return (
    <VideoAnalyticsContext.Provider
      value={{
        selectedAnalyticTab,
        setSelectedAnalyticTab,
        isAllBCBAFetching,
        isAllRBTFetching,
        allBCBAList,
        allRBTList,
        selectedDialogTab,
        setSelectedDialogTab,
        sessionClients,
        isSessionClientsLoading,
        selectedDates,
        setSelectedDates,
        selectedBCBA,
        setSelectedBCBA,
        selectedRBTs,
        setSelectedRBTs,
        selectedRBTDates,
        setSelectedRBTDates,
      }}
    >
      {children}
    </VideoAnalyticsContext.Provider>
  );
};
